import { useEffect, useMemo, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import type { LogClient, LogEntry } from '../core/log/client';
import { encodeMerkleTreeLeaf } from '../core/rfc6962/leaf';
import { leafIndexFromExtensions } from '../core/rfc6962/sct';
import { embeddedSctEntry, verifyScts, type SctVerdict } from '../core/sct/embedded';
import { parseCertificate, parseTbs, type ParsedCert } from '../core/x509/parse';
import { currentRoutePath, navigate, withFrom } from '../router';
import { Badge } from '../ui/components/Badge';
import { CertSummary } from '../ui/components/CertSummary';
import { ChainView } from '../ui/components/ChainView';
import { ErrorBox } from '../ui/components/ErrorBox';
import { ExtensionList } from '../ui/components/ExtensionList';
import { Hash } from '../ui/components/Hash';
import { Icon } from '../ui/components/Icon';
import { LogLayout } from '../ui/components/LogLayout';
import { RawTabs, type RawItem } from '../ui/components/RawTabs';
import { Card, Section } from '../ui/components/Section';
import { SctTable } from '../ui/components/SctTable';
import { Spinner } from '../ui/components/Spinner';
import { SubTabs } from '../ui/components/Tabs';
import { TimeStamp } from '../ui/components/TimeStamp';
import { pemEncode } from '../core/bytes/encoding';
import { setSubmitPrefill } from '../ui/state/prefill';
import { fmtInt, parseIndex } from '../ui/format';
import { useAsync, type AsyncState } from '../ui/hooks/useAsync';
import { useLogClient } from '../ui/hooks/useLogClient';
import { inclusionLinksFor } from '../ui/sctLinks';
import { registry } from '../ui/state/logs';
import { refreshTreeHead, treeHeads } from '../ui/state/treeHeads';
import { LoadingPage, NotFoundLog } from './Log';
import { logPath, logRoute } from '../ui/nav';
import { ReturnStrip } from '../ui/components/ReturnStrip';
import { useInclusionChecks } from '../ui/hooks/useInclusionChecks';
import type { Sct, SignedEntry } from '../core/rfc6962/types';
import { ExportMenu } from '../ui/components/Export';

export function EntryPage({ params, query }: PageProps) {
  const index = parseIndex(params.index ?? '');
  const { log, client, loading } = useLogClient(params);
  const id = log?.id ?? '';
  const th = treeHeads.value[id];
  const state = useAsync(
    async (signal) => {
      if (!client || index === undefined) return undefined;
      void refreshTreeHead(client).catch(() => undefined);
      const [e] = await client.getEntries(index, 1, { signal });
      if (!e) throw new Error(`The log returned no entry at index ${index}`);
      return e;
    },
    [client, index],
  );
  const [jump, setJump] = useState('');

  // ← / → move between neighbouring entries.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (index === undefined || (ev.target as HTMLElement)?.tagName === 'INPUT' || (ev.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (!log) return;
      if (ev.key === 'ArrowLeft' && index > 0) navigate(logRoute(log, `entry/${index - 1}`));
      if (ev.key === 'ArrowRight' && (!th || index + 1 < th.treeSize)) navigate(logRoute(log, `entry/${index + 1}`));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [log, index, th?.treeSize]);

  if (loading) return <LoadingPage />;
  if (!log) return <NotFoundLog id={`${params.op}/${params.log}`} />;
  const e = state.value;
  const filePrefix = `${log.description.replace(/\W+/g, '_')}-${index ?? 'x'}`;
  const pageStart = index !== undefined && client ? Math.floor(index / client.pageSize) * client.pageSize : 0;

  return (
    <LogLayout log={log} {...(client ? { client } : {})} tab="entries" trail={[{ label: 'Entries', href: logPath(log, 'entries', { start: pageStart }) }, { label: index !== undefined ? `#${fmtInt(index)}` : String(params.index) }]}>
      <ReturnStrip query={query} />
      {client && index === undefined && <ErrorBox error={new Error(`"${params.index}" is not a valid entry index`)} />}
      {client && index !== undefined && (
        <>
          <div class="entry-toolbar">
            <div class="nav">
              <a class="btn btn-sm btn-ghost" href={logPath(log, 'entries', { start: pageStart })}><Icon name="arrowLeft" /> Entries</a>
              <span class="sep" />
              <span class="stepper">
                <a class="btn btn-sm" href={logPath(log, `entry/${Math.max(0, index - 1)}`)} aria-disabled={index === 0} title="Previous entry (←)"><Icon name="chevronLeft" /></a>
                <span class="idx">#{fmtInt(index)}</span>
                <a class="btn btn-sm" href={logPath(log, `entry/${index + 1}`)} aria-disabled={th !== undefined && index + 1 >= th.treeSize} title="Next entry (→)"><Icon name="chevronRight" /></a>
              </span>
              {e && (e.entryType === 'precert' ? <Badge tone="info">precertificate</Badge> : <Badge tone="muted">certificate</Badge>)}
              <form class="row" style="gap:0.3rem" onSubmit={(ev) => { ev.preventDefault(); const n = parseIndex(jump); if (n !== undefined) { navigate(logRoute(log, `entry/${n}`)); setJump(''); } }}>
                <input class="input input-sm mono" style="width:11ch" placeholder="go to #" value={jump} onInput={(ev) => setJump((ev.target as HTMLInputElement).value)} aria-label="Go to entry" />
              </form>
            </div>
            <div class="actions">
              {e && (e.entryType === 'x509' || e.precert) && (
                <button
                  type="button"
                  class="btn btn-sm"
                  title={e.entryType === 'precert' ? 'Replay this precertificate into other logs with add-pre-chain' : 'Submit this certificate to other logs with add-chain'}
                  onClick={async () => {
                    const ders: Uint8Array[] = [e.entryType === 'x509' ? e.cert : e.precert!];
                    for (const ref of e.chain) ders.push('der' in ref ? ref.der : await client.resolveIssuer!(ref.fingerprint));
                    setSubmitPrefill(ders.map((d) => pemEncode(d)).join(''));
                    navigate('/submit', { from: currentRoutePath() });
                  }}
                >
                  <Icon name="upload" /> <span>Submit<span class="long"> to other logs</span></span>
                </button>
              )}
              {e && (() => { const der = e.entryType === 'x509' ? e.cert : e.precert ?? e.cert; const what = e.entryType === 'x509' ? 'Certificate' : e.precert ? 'Precertificate' : 'TBS'; return <ExportMenu label={what} icon="file" formats={[{ label: `${what} (PEM)`, filename: `${filePrefix}.pem`, mime: 'application/x-pem-file', text: () => pemEncode(der) }, { label: `${what} (DER)`, filename: `${filePrefix}.der`, mime: 'application/pkix-cert', bytes: der }]} />; })()}
              <a class="btn btn-sm btn-primary" href={withFrom(logPath(log, 'inclusion', { index, size: th?.treeSize }))}><Icon name="shield" /> Verify inclusion</a>
            </div>
          </div>
          {state.status === 'error' && <ErrorBox error={state.error} title="Could not fetch this entry" />}
          {state.status === 'loading' && !e && <Spinner label="Fetching entry…" />}
          {e && <EntryBody entry={e} logId={id} filePrefix={filePrefix} client={client} />}
        </>
      )}
    </LogLayout>
  );
}

type View = 'cert' | 'precert' | 'chain' | 'raw';

/** The SCTs embedded in a final certificate: signature verdicts, plus (on demand) an inclusion check in every issuing log. */
function EmbeddedScts({ scts, sctCheck, logId, index }: { scts: Sct[]; sctCheck: AsyncState<{ verdicts: SctVerdict[]; links: Array<string | undefined>; problem: string | undefined; entry: SignedEntry | undefined } | undefined>; logId: string; index: number }) {
  const entries = useMemo(() => scts.map(() => sctCheck.value?.entry), [scts, sctCheck.value?.entry]);
  const { checks, summary, running, run } = useInclusionChecks(`entry:${logId}:${index}:checks`, scts, entries);
  const started = checks.length === scts.length;
  return (
    <Card
      title={<><Icon name="shield" /> Embedded SCTs <span class="badge badge-muted">{scts.length}</span>{started && !running && <Badge tone={summary.included === summary.total ? 'ok' : summary.included > 0 ? 'info' : 'warn'}>{summary.included} of {summary.total} logs confirmed</Badge>}</>}
      actions={running ? <Spinner label="Checking inclusion…" /> : <button type="button" class="btn btn-sm" disabled={sctCheck.status === 'loading'} onClick={() => run(started)} title="Fetch an inclusion proof from every issuing log and verify it against the log's signed tree head"><Icon name="shield" /> {started ? 'Re-check' : 'Check inclusion in all logs'}</button>}
      foot={sctCheck.value?.problem ? sctCheck.value.problem : 'These SCTs were issued for the precertificate. "Logged entry" links to that precert entry in each log; each signature is checked with the log\'s key over the reconstructed precert entry.'}
    >
      {sctCheck.status === 'loading' && <Spinner label="Verifying SCT signatures…" />}
      <SctTable scts={scts} {...(sctCheck.value ? { verdicts: sctCheck.value.verdicts, inclusionLinks: sctCheck.value.links, entry: sctCheck.value.entry } : {})} {...(started ? { checks } : {})} />
    </Card>
  );
}

function EntryBody({ entry, logId, filePrefix, client }: { entry: LogEntry; logId: string; filePrefix: string; client: LogClient }) {
  let parsed: ParsedCert | undefined;
  let parseError: string | undefined;
  try {
    parsed = entry.entryType === 'x509' ? parseCertificate(entry.cert) : parseTbs(entry.cert);
  } catch (err) {
    parseError = err instanceof Error ? err.message : String(err);
  }
  let precert: ParsedCert | undefined;
  try {
    if (entry.precert) precert = parseCertificate(entry.precert);
  } catch {
    /* shown as raw only */
  }
  const leafIdxExt = leafIndexFromExtensions(entry.leaf.entry.extensions);
  const [view, setView] = useState<View>('cert');
  // Final certificates carry SCTs from their precert; verify them against the chain's CA key.
  const sctCheck = useAsync(
    async (signal) => {
      if (entry.entryType !== 'x509' || !parsed || parsed.scts.length === 0) return undefined;
      const issuers: Uint8Array[] = [];
      for (const ref of entry.chain.slice(0, 2)) {
        if ('der' in ref) issuers.push(ref.der);
        else if (client.resolveIssuer) issuers.push(await client.resolveIssuer(ref.fingerprint, signal));
      }
      const ctx = await embeddedSctEntry(entry.cert, issuers);
      const reg = registry.value;
      const verdicts: SctVerdict[] = await verifyScts(ctx.leaf.scts, ctx.entry, (id) => reg.byLogId(id)?.key);
      const links = await inclusionLinksFor(ctx.leaf.scts, ctx.entry);
      return { verdicts, links, problem: ctx.problem, entry: ctx.entry };
    },
    [entry],
  );
  const raw: RawItem[] = [
    { label: 'Leaf input (MerkleTreeLeaf)', data: encodeMerkleTreeLeaf(entry.leaf.entry), filename: `${filePrefix}-leaf` },
    entry.entryType === 'x509'
      ? { label: 'Certificate DER', data: entry.cert, pemLabel: 'CERTIFICATE', filename: `${filePrefix}-cert` }
      : { label: 'TBSCertificate (poison-free)', data: entry.cert, filename: `${filePrefix}-tbs` },
  ];
  if (entry.precert) raw.push({ label: 'Precertificate DER', data: entry.precert, pemLabel: 'CERTIFICATE', filename: `${filePrefix}-precert` });

  const tabs: Array<{ key: View; label: string; count?: number }> = [
    { key: 'cert', label: entry.entryType === 'precert' ? 'Precert TBS (as logged)' : 'Certificate' },
    ...(precert ? [{ key: 'precert' as View, label: 'Precertificate (as submitted)' }] : []),
    { key: 'chain', label: 'Issuer chain', count: entry.chain.length },
    { key: 'raw', label: 'Raw bytes' },
  ];

  return (
    <div class="stack">
      <Card title={<><Icon name="tree" /> Log entry</>}>
        <div class="stats">
          <div class="stat"><div class="label">Logged</div><div class="value" style="font-size:0.95rem"><TimeStamp ms={entry.leaf.entry.timestamp} relative={false} /></div><div class="sub"><TimeStamp ms={entry.leaf.entry.timestamp} /></div></div>
          <div class="stat"><div class="label">Subject</div><div class="value" style="font-size:0.95rem" title={parsed?.subject}>{parsed?.commonName ?? '—'}</div><div class="sub">{parsed ? `${parsed.sans.length} alt name${parsed.sans.length === 1 ? '' : 's'}` : parseError}</div></div>
          <div class="stat"><div class="label">Issuer</div><div class="value" style="font-size:0.95rem" title={parsed?.issuer}>{parsed ? /CN=([^,]+)/.exec(parsed.issuer)?.[1] ?? parsed.issuer : '—'}</div><div class="sub">{parsed ? (parsed.notAfter.getTime() < Date.now() ? 'expired ' : 'expires ') + parsed.notAfter.toISOString().slice(0, 10) : ''}</div></div>
        </div>
        <dl class="kv" style="margin-top:0.9rem">
          <dt>Leaf hash</dt>
          <dd><Hash value={entry.leafHash} /></dd>
          {entry.issuerKeyHash && <><dt>Issuer key hash</dt><dd class="row"><Hash value={entry.issuerKeyHash} /><span class="tiny muted">SHA-256 of the issuing CA's public key</span></dd></>}
          {entry.leaf.entry.extensions.length > 0 && (
            <><dt>CT extensions</dt><dd class="row"><Hash value={entry.leaf.entry.extensions} />{leafIdxExt !== undefined && <span class="small">leaf_index = {fmtInt(leafIdxExt)}</span>}</dd></>
          )}
        </dl>
      </Card>

      {parsed && parsed.scts.length > 0 && <EmbeddedScts scts={parsed.scts} sctCheck={sctCheck} logId={logId} index={entry.index} />}

      <div class="card">
        <div class="card-head">
          <SubTabs items={tabs} value={view} onChange={setView} />
        </div>
        <div class="card-body">
          {view === 'cert' && (
            <>
              {parseError && <ErrorBox error={parseError} title="Could not parse the certificate" />}
              {parsed && (
                <>
                  <CertSummary cert={parsed} />
                  <h3 style="margin:1.1rem 0 0.5rem">Extensions ({parsed.extensions.length})</h3>
                  <ExtensionList extensions={parsed.extensions} />
                  {parsed.sctParseError && <div class="small muted">SCT list could not be parsed: {parsed.sctParseError}</div>}
                </>
              )}
            </>
          )}
          {view === 'precert' && precert && (
            <>
              <p class="small muted">The full precertificate from <code>extra_data</code>, including the poison extension and the CA's signature.</p>
              <CertSummary cert={precert} />
              <h3 style="margin:1.1rem 0 0.5rem">Extensions ({precert.extensions.length})</h3>
              <ExtensionList extensions={precert.extensions} />
            </>
          )}
          {view === 'chain' && <ChainView chain={entry.chain} client={client} filePrefix={filePrefix} />}
          {view === 'raw' && <RawTabs items={raw} />}
        </div>
      </div>
      <p class="tiny muted">Tip: use <span class="kbd">←</span> <span class="kbd">→</span> to move between entries.</p>
      <Section title="Where does this come from?" open={false}>
        <p class="small">
          {entry.entryType === 'precert'
            ? 'This is a precertificate entry: the log stores the TBSCertificate with the poison extension removed plus the hash of the CA key (RFC 6962 §3.2). The precertificate as submitted (with poison) comes from extra_data.'
            : 'This is an X.509 entry: the final certificate as submitted with add-chain, followed by its issuer chain.'}
          {client.kind === 'tiled' ? ' For this tiled log the entry was read from a data tile and the issuer chain is referenced by fingerprints resolved via /issuer/.' : ' It was fetched with get-entries.'}
        </p>
      </Section>
    </div>
  );
}
